import React, { createRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Divider, Drawer, Tab, Typography, useTheme } from '@mui/material';
import { TabList, TabContext } from '@mui/lab';
import { AccountBalance } from '@mui/icons-material';
import { useAuth } from 'hooks';
import { useUi } from 'hooks/useUi';
import { verticalHeightBreakpoint } from 'lib/constants';
import styles from './MainMenuBar.module.scss';
import { getMenuTabs } from 'helpers/general';

export interface MainMenuBarProps {
    onMenuToggle: () => void;
}

const simplePaths = [
    '/billing',
    '/inventory',
    '/accounts',
    '/live-reporting',
    '/team',
    '/vehicles',
    '/efficiency',
    '/issues',
    '/map',
    '/consumers',
];

const translatePathToTabValue = (path: string) => {
    if (path.startsWith('/planning')) return '/planning/list';
    if (path.startsWith('/monitors')) return '/monitors/overview';
    if (path.startsWith('/suppliers')) return '/suppliers';
    if (path.startsWith('/organization')) return '/organization';
    if (path.startsWith('/efficiency')) return '/efficiency/trends';
    if (simplePaths.includes(path)) return path;
    return 'disabled';
};

const verticallyCompressedTabStyle = {
    minHeight: '42px',
    textAlign: 'left',
    justifyContent: 'flex-start',
    maxWidth: '140px',
    fontSize: '0.7rem',
};

export default function MainMenuBar(props: MainMenuBarProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { ui } = useUi();
    const theme = useTheme();
    const [height, setHeight] = useState(ui.height);

    const reference = createRef<HTMLDivElement>();
    const onMenuToggle = props.onMenuToggle;
    const [tabValue, setTabValue] = useState(
        translatePathToTabValue(location.pathname)
    );

    const tabs = getMenuTabs(user?.protected?.supplierId);
    const adminTabs = [
        {
            label: 'Suppliers',
            icon: <AccountBalance />,
            value: '/suppliers',
        },
    ];

    const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
        setTabValue(translatePathToTabValue(newValue));
        navigate(newValue);
    };

    useEffect(() => {
        setHeight(ui.height);
    }, [ui.height]);

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            if (
                reference.current &&
                !reference.current.contains(event.target as Node)
            )
                onMenuToggle();
        };
        window.addEventListener('mousedown', handleClick);
        return () => {
            window.removeEventListener('mousedown', handleClick);
        };
    }, [reference, onMenuToggle]);
    useEffect(() => {
        setTabValue(translatePathToTabValue(location.pathname));
    }, [location.pathname]);

    const mainMenuBarIndicatorStyles = {
        backgroundColor: theme.MainMenuBarIndicator?.backgroundColor || 'white',
    };

    const adminSectionDividerStyles = {
        borderColor: theme.AdminSectionDivider?.borderColor || 'white',
    };

    return (
        <Drawer
            variant={ui.isMobile ? 'temporary' : 'permanent'}
            open={!ui.isMobile || !!ui.menuBarActive}
            className={styles.container}
            onClose={onMenuToggle}
            data-testid="MainMenuBar"
        >
            <div
                className={styles['inner-container']}
                style={{ backgroundColor: theme.palette.secondary.main }}
                ref={reference}
            >
                {!ui.isMobile && <div className={styles.topSpacer} />}
                <TabContext value={tabValue}>
                    <TabList
                        orientation="vertical"
                        onChange={handleTabChange}
                        TabIndicatorProps={{
                            style: mainMenuBarIndicatorStyles,
                        }}
                        textColor="inherit"
                        variant="scrollable"
                        scrollButtons={false}
                        sx={{ marginBottom: '1rem' }}
                    >
                        {height >= verticalHeightBreakpoint &&
                            tabs.map((tab) => {
                                const icon = <tab.icon />;
                                return (
                                    <Tab
                                        onClick={() => {
                                            if (tab.value === tabValue) {
                                                onMenuToggle();
                                            }
                                        }}
                                        key={tab.value}
                                        {...tab}
                                        icon={icon}
                                    />
                                );
                            })}
                        {height < verticalHeightBreakpoint &&
                            tabs.map((tab) => {
                                const icon = <tab.icon />;
                                return (
                                    <Tab
                                        onClick={() => {
                                            if (tab.value === tabValue) {
                                                onMenuToggle();
                                            }
                                        }}
                                        key={tab.value}
                                        {...tab}
                                        icon={icon}
                                        iconPosition="start"
                                        sx={verticallyCompressedTabStyle}
                                    />
                                );
                            })}
                        {user?.protected?.supplierId?.toString() === '0' && (
                            <Tab
                                label=""
                                style={{
                                    opacity: 1,
                                    padding: 0,
                                }}
                                icon={
                                    <Divider
                                        style={{
                                            color: '#fafafa',
                                            width: '100%',
                                        }}
                                        textAlign="left"
                                        sx={{
                                            '&::before, &::after':
                                                adminSectionDividerStyles,
                                        }}
                                    >
                                        <Typography
                                            sx={{ color: 'white' }}
                                            variant="overline"
                                        >
                                            Admin
                                        </Typography>
                                    </Divider>
                                }
                                disabled
                            />
                        )}
                        {user?.protected?.supplierId?.toString() === '0' &&
                            height >= verticalHeightBreakpoint &&
                            adminTabs.map((tab) => (
                                <Tab key={tab.value} {...tab} />
                            ))}
                        {user?.protected?.supplierId?.toString() === '0' &&
                            height < verticalHeightBreakpoint &&
                            adminTabs.map((tab) => (
                                <Tab
                                    key={tab.value}
                                    {...tab}
                                    iconPosition="start"
                                    sx={verticallyCompressedTabStyle}
                                />
                            ))}
                        {user?.protected?.supplierId?.toString() !== '0' && (
                            <Tab label="" disabled value="/suppliers" />
                        )}
                        <Tab
                            key="disabled"
                            value="disabled"
                            label=""
                            disabled
                            sx={{ minHeight: 0, padding: 0 }}
                        />
                    </TabList>
                </TabContext>
            </div>
        </Drawer>
    );
}
